import { useMemo } from 'react';
import { Link } from 'react-router';
import { AlertCircle, CheckCircle, Clock, type LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { ListStatus } from '@/components/list/ListStatus';
import { reviewerOf } from '@/components/timesheet/reviewActions';
import { useLoader } from '@/hooks/useLoader';
import { formatMinutes } from '@/lib/duration';
import type PeriodModel from '@/models/entities/PeriodModel';
import { useCompanyStore, useCurrentCompany } from '@/stores/useCompanyStore';
import { useReferenceOptions } from '@/stores/useReferenceOptions';
import { TIME_SHEET_STATUS } from '@/models/references/references';
import { validationLink } from '@/pages/validation/validationQuery';
import { CollaboratorBreakdown } from './CollaboratorBreakdown';
import { dashboardTiles, loadLatestPeriod, loadStatusFigures, type StatusFigures } from './dashboardData';

/** Clé du chargement : société et équipes du manager (vide : le Dirigeant) */
async function loadDashboard(key: string): Promise<{ period: PeriodModel | null; figures: StatusFigures[] }> {
  const [companyId, teams] = key.split('|');
  const period = await loadLatestPeriod(Number(companyId));
  const teamIds = teams ? teams.split(',').map(Number) : null;

  return { period, figures: period ? await loadStatusFigures(Number(period.id), teamIds) : [] };
}

/**
 * Tableau de bord (§4.7) du Dirigeant et du manager : la même vue, celle du manager limitée à ses équipes. Chiffres de
 * la période en cours (la dernière de la société ; une autre période : son récapitulatif dans l'Historique), en une
 * requête (GET /time_sheets/_aggregated_data) : demandes et heures en attente (dont du Dirigeant, dont du manager),
 * heures traitées (feuilles validées). Chaque tuile ouvre la Validation filtrée (en attente : du manager et du
 * dirigeant ; traitées : validées). En dessous, la répartition par collaborateur (CollaboratorBreakdown).
 */
export function DashboardPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);
  const reviewer = reviewerOf(company);
  const scope = reviewer.isDirector ? '' : reviewer.managedTeamIds.join(',');
  const { data, error, loaded } = useLoader(company ? `${company.id}|${scope}` : null, loadDashboard);
  const statuses = useReferenceOptions('timeSheetStatuses');
  const teamIds = useMemo(() => (scope ? scope.split(',').map(Number) : null), [scope]);

  if (companiesLoaded && !company) {
    return <NoCompanySelected />;
  }
  if (!data || !statuses.length) {
    return <ListStatus loading={!loaded || !statuses.length} error={error} />;
  }
  if (!data.period) {
    return <ListStatus empty="Aucune période pour cette société." />;
  }

  const tiles = dashboardTiles(data.figures, statuses);

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500" data-testid="dashboard-scope">
        Période en cours : <span className="font-semibold text-slate-900">{data.period.monthLabel}</span>
        {scope ? ' · vos équipes' : ' · toutes les équipes'}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatTile
          title="Demandes en attente"
          value={String(tiles.pending.count)}
          icon={AlertCircle}
          tone="amber"
          to={PENDING_LINK}
          details={[
            `dont ${tiles.pending.director} en attente du dirigeant`,
            `dont ${tiles.pending.manager} en attente du manager`,
          ]}
        />
        <StatTile
          title="Heures en attente"
          value={formatMinutes(tiles.pendingMinutes.total)}
          icon={Clock}
          tone="blue"
          to={PENDING_LINK}
          details={[
            `dont ${formatMinutes(tiles.pendingMinutes.director)} en attente du dirigeant`,
            `dont ${formatMinutes(tiles.pendingMinutes.manager)} en attente du manager`,
          ]}
        />
        <StatTile
          title="Heures traitées"
          value={formatMinutes(tiles.done.minutes)}
          icon={CheckCircle}
          tone="green"
          to={validationLink([TIME_SHEET_STATUS.VALIDATED])}
          details={[`${tiles.done.count} demande${tiles.done.count > 1 ? 's' : ''}`]}
        />
      </div>
      <CollaboratorBreakdown periodId={Number(data.period.id)} teamIds={teamIds} />
    </div>
  );
}

/** Les feuilles en attente : du manager et du dirigeant */
const PENDING_LINK = validationLink([TIME_SHEET_STATUS.PENDING_MANAGER, TIME_SHEET_STATUS.PENDING_DIRECTOR]);

const TONES = {
  amber: { icon: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
  blue: { icon: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
  green: { icon: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' },
} as const;

/** Tuile de chiffre (maquette) : intitulé, valeur, icône, détail en dessous ; ouvre la liste qui détaille le chiffre */
function StatTile({
  title,
  value,
  icon: Icon,
  tone,
  details,
  to,
}: {
  title: string;
  value: string;
  icon: LucideIcon;
  tone: keyof typeof TONES;
  details: string[];
  /** Liste filtrée de la Validation */
  to: string;
}) {
  const colors = TONES[tone];
  return (
    <Link
      to={to}
      className={clsx(
        'block bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow focus-visible:outline-2 focus-visible:outline-blue-600',
        colors.border,
      )}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-slate-700 text-sm font-semibold mb-1">{title}</h3>
          <p className="text-3xl font-bold text-slate-900" data-testid="stat-value">
            {value}
          </p>
        </div>
        <div className={clsx('p-2 rounded-lg', colors.bg)}>
          <Icon className={clsx('w-6 h-6', colors.icon)} />
        </div>
      </div>
      <ul className="space-y-0.5 text-sm text-slate-400">
        {details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
    </Link>
  );
}
